/**
 * HomePage.jsx - Landing Page Component
 * 
 * @description
 * The main landing page for Link Spaghetti application. 
 * Showcases product features, pricing, and encourages user sign-up.
 * 
 * @route /
 * @auth Not required
 * 
 * @sections
 * - Navbar: Main navigation with login/signup buttons
 * - Hero: Main value proposition with animated phone mockup
 * - Features: 6 key features of the platform
 * - HowItWorks: 3-step process to get started
 * - Pricing: Three pricing tiers (Free, Pro, Max)
 * - CTA: Call-to-action section
 * - Footer: Company information and links
 * 
 * @components
 * - Navbar: Sticky navigation with mobile menu
 * - Hero: Landing hero section with animations
 * - Features: Feature grid display
 * - HowItWorks: Step-by-step guide
 * - Pricing: Pricing tier comparison
 * - CTA: Bottom call-to-action
 * - Footer: Site footer with links
 * 
 * @features
 * - Smooth scroll navigation
 * - Responsive design
 * - Animated elements on scroll
 * - Mobile-optimized layout
 * 
 * @dependencies
 * All components are imported from the components directory
 */

import { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import Features from '../components/Features';
import HowItWorks from '../components/HowItWorks';
import Pricing from '../components/Pricing';
import CTA from '../components/CTA';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { fetchPublicHomePageContent } from '../services/api';

const HomePage = () => {
  const [pageContent, setPageContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadHomePageContent = async () => {
      try {
        setIsLoading(true);
        const content = await fetchPublicHomePageContent();
        if (content) {
          setPageContent(content);
          setError(null);
        } else {
          console.error('Received empty content from API');
          setError('Failed to load page content. Please try again later.');
        }
      } catch (err) {
        console.error('Failed to load homepage content:', err);
        setError('Failed to load page content. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadHomePageContent();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LoadingSpinner />
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white flex-col">
        <div className="text-red-500 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Show maintenance mode message if enabled
  if (pageContent?.settings?.maintenanceMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white flex-col p-4 text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Site Under Maintenance</h1>
        <p className="text-lg text-gray-600 max-w-md mb-8">
          We're currently updating our site to serve you better. Please check back soon!
        </p>
        <p className="text-sm text-gray-500">
          If you're an administrator, please <a href="/admin/login" className="text-blue-500 hover:underline">log in</a> to access the site.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {pageContent?.navbar ? (
        <Navbar links={pageContent.navbar.links} />
      ) : (
        <Navbar links={[]} />
      )}
      
      {/* Wrap Hero in error boundary logic */}
      <div className="hero-section">
        {pageContent?.hero ? (
          <Hero content={pageContent.hero} />
        ) : (
          <Hero content={null} />
        )}
      </div>
      
      <Features content={pageContent?.features} />
      <HowItWorks content={pageContent?.howItWorks} />
      {/* Show pricing unless explicitly disabled */}
      {(pageContent?.settings?.showPricing !== false) && <Pricing content={pageContent?.pricing} />}
      <CTA content={pageContent?.cta} />
      <Footer content={pageContent?.footer} />
    </div>
  );
};

export default HomePage; 